import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { userIsLoggedIn, userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import { useDispatch, useSelector } from "react-redux";
import { selectWishlistAmount } from "~/features/wishlist/redux/wishlistSelectors";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { useLoadWishlistsOnMount } from "~/features/wishlist/utils/wishlistHelper";
import { canSeeFCC, canSeeMyCompany } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { MEDIA_QUERY_MIN_TABLET } from "@schaeffler-commerce/design-system";
import { NotificationNavItem } from "~/features/navigation/components/Notifications/NotificationNavItem";
import { useProductComparisonCount } from "~/features/compareProducts/api/productComparisonQuery";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { HeaderMinicart } from "~/features/cart/minicart/MinicartComponent";
import { DesktopScanDMCTeaser } from "~/features/navigation/components/DesktopScanDMCTeaser/DesktopScanDMCTeaser";
import { NavItems } from "~/features/navigation/components/headerMenu/PersonalNavItems";
import { PersonalNavItem } from "~/features/navigation/components/headerMenu/PersonalNavItem";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import { styled } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import { useComparisonDialog } from "~/features/compareProducts/hooks/useComparisonDialog";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

export const canSeeSalesFinder = () =>
  !getRuntimeConfig().removeSalesPartnerFinder && (!userIsLoggedIn() || userIsRegistered());

export const MobilePersonalNav = () => {
  const { openComparisonDialog } = useComparisonDialog();
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const canSeeCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  useLoadWishlistsOnMount();

  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  const wishlistAmount = useSelector(selectWishlistAmount);

  const wishlistItem = (
    <PersonalNavItem
      name={t("wishlistPage.title")}
      icon={<BookmarkBorderOutlinedIcon />}
      data-test-id={"header-btn-wishlist"}
      badgeContent={wishlistAmount !== undefined ? wishlistAmount : ""}
    />
  );

  const { count: listItems } = useProductComparisonCount();

  const onComparisonClick = () => {
    if (userIsPublic()) {
      dispatch(openAuthOverlay("login"));
      return;
    }
    openComparisonDialog();
  };

  const onClick = (e: React.MouseEvent) => {
    if (userIsPublic()) {
      e.preventDefault();
      dispatch(openAuthOverlay("login"));
    }
  };

  return (
    <PersonalNavRoot className="scha__personal-nav">
      <PersonalNavList className="scha__personal-nav__list">
        {isMinTablet && canSeeFCC() && (
          <li className="scha__personal-nav__list__item">
            <NavItems.FCC />
          </li>
        )}
        {isMinTablet && !canSeeFCC() && (
          <li className="scha__personal-nav__list__item">
            <DesktopScanDMCTeaser />
          </li>
        )}
        {canSeeMyCompany() && (
          <li className="scha__personal-nav__list__item">
            <NavItems.MyCompany />
          </li>
        )}
        {isMinTablet && canSeeSalesFinder() && (
          <li className="scha__personal-nav__list__item">
            <RouterLink to="/store-finder" data-test-id={"header-sales-partner"}>
              <PersonalNavItem
                data-test-id={"header-btn-sales-partner"}
                icon={<LocationOnOutlinedIcon />}
                name={t("salesPartner.title")}
              />
            </RouterLink>
          </li>
        )}
        <NotificationNavItem />
        <li className="scha__personal-nav__list__item">
          <PersonalNavItem
            icon={<CompareOutlinedIcon />}
            badgeContent={listItems}
            name={t("header.link.comparison.list")}
            onClick={onComparisonClick}
          />
        </li>
        {isMinTablet && canSeeWishlistButton() && (
          <li className="scha__personal-nav__list__item">
            {userIsPublic() ? (
              <a id="home_wishlist_btn" data-test-id={"header-wishlist"} onClick={onClick}>
                {wishlistItem}
              </a>
            ) : (
              <RouterLink to="/wishlist" id="home_wishlist_btn" data-test-id={"header-wishlist"} onClick={onClick}>
                {wishlistItem}
              </RouterLink>
            )}
          </li>
        )}
        {canSeeCart && (
          <li className="scha__personal-nav__list__item">
            <HeaderMinicart />
          </li>
        )}
      </PersonalNavList>
    </PersonalNavRoot>
  );
};

const PersonalNavRoot = styled("div")({
  height: "100%",
  width: "100%",
});

const PersonalNavList = styled("ul")(({ theme }) => ({
  display: "flex",
  height: "inherit",
  justifyContent: "flex-end",
  gap: theme.spacing(0.5),
  paddingLeft: 0,
  "& > .scha__personal-nav__list__item": {
    display: "flex",
    alignItems: "center",
    listStyle: "none",
  },
}));
